"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface RichtextFieldProps {
	id: string;
	/** Visible field label; omit or pass empty when toolbar carries the title. */
	label?: string;
	/** Accessible name for the editor host (defaults to `label`). */
	ariaLabel?: string;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	/** Host slot for TipTap (wired in P3/P4). */
	editorSlot?: ReactNode;
	toolbarSlot?: ReactNode;
	/** Override default editor host chrome (e.g. borderless when nested in EditableField). */
	editorClassName?: string;
}

/**
 * Scaffold host for richtext. TipTap wiring completes in P3/P4.
 */
export function RichtextField({
	id,
	label,
	ariaLabel,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	disabled = false,
	layoutHint,
	className,
	editorSlot,
	toolbarSlot,
	editorClassName,
}: RichtextFieldProps) {
	const effectiveAriaLabel = ariaLabel ?? label ?? id;

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(disabled && "pointer-events-none opacity-50", className)}
		>
			{name ? <input type="hidden" name={name} readOnly /> : null}
			{toolbarSlot ? (
				<div data-richtext-toolbar className="mb-2">
					{toolbarSlot}
				</div>
			) : null}
			<div
				id={id}
				data-field-type="richtext"
				data-tiptap-host
				className={cn(
					"min-h-[8rem] rounded-md border border-input bg-card p-3 text-sm text-muted-foreground",
					editorClassName,
				)}
				role="textbox"
				aria-multiline
				aria-label={effectiveAriaLabel}
				aria-invalid={Boolean(error) ? true : undefined}
				aria-disabled={disabled || undefined}
				tabIndex={disabled ? -1 : 0}
				onBlur={onBlur}
			>
				{editorSlot ?? "Rich text editor host (TipTap adapter in P4)"}
			</div>
		</FieldShell>
	);
}
